/* screens/support/SupportThreadDrawer.jsx — операторский просмотр заявки:
   карточка клиента (контакты + аккаунт) + переписка + ответ (с файлами) +
   закрытие с ВНУТРЕННЕЙ причиной (пользователю не видна) → пользователю
   авто-уходит благодарность. */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Textarea } from '../../components/ui/Field.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import * as api from '../../lib/api/support.js';
import { MessageList, Composer } from './SupportChat.jsx';

const { useState, useEffect, useRef, useCallback } = React;

const STATUS = {
  open: { label: 'Ждут ответа', tone: 'accent' },
  pending: { label: 'Ожидание', tone: 'neutral' },
  closed: { label: 'Закрыта', tone: 'neutral' },
};

const WS_KIND = { personal: 'Личное', business: 'Бизнес' };
const fmtDay = (iso) => (iso
  ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '');

/** Строка «подпись — значение». Значение-ссылка (tel/mailto) даёт оператору
 *  позвонить/написать в один клик, не переписывая номер руками. */
function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(84px, auto) 1fr',
                  gap: 8, alignItems: 'baseline' }}>
      <span className="t-caption text-mute">{label}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

const Missing = () => <span className="t-caption text-mute">не указан</span>;

/** Карточка клиента: с кем оператор говорит и как до него дозвониться.
 *  Гость и зарегистрированный различаются явно — «Гость» без аккаунта не значит
 *  «чужой»: он может быть нашим клиентом, просто написал не заходя в кабинет. */
function ClientCard({ c }) {
  // Досье свёрнуто по умолчанию: у владельца сети пространств бывает полтора
  // десятка, и развёрнутый список уводил переписку — то, ради чего оператор
  // и открыл заявку, — далеко под фолд (на 380px за пределы экрана целиком).
  const [openDossier, setOpenDossier] = useState(false);
  if (!c) return null;
  const initial = ((c.name || c.email || 'Г')[0] || '?').toUpperCase();
  const ws = c.workspaces || [];
  // Считаем ДЕЙСТВУЮЩИЕ оплаты: свёрнутая сводка — единственное, что оператор
  // видит без клика, и «платных 2» по просроченным тарифам прямо толкает его
  // сказать «у вас всё оплачено» отвалившемуся клиенту.
  const paid = ws.filter((w) => w.is_paid_now).length;
  return (
    <div className="stack stack-2" style={{ border: '1px solid var(--border, #e5e5e5)',
         borderRadius: 10, padding: '10px 12px', background: 'var(--surface-2, #f8fafc)' }}>
      <div className="cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 8, flex: '0 0 auto',
              overflow: 'hidden', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13,
              background: 'var(--accent-soft, #eef2ff)', color: 'var(--accent, #2f6bff)' }}>
          {c.avatar
            ? <img src={c.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : initial}
        </span>
        <span className="grow truncate" style={{ minWidth: 0, fontWeight: 600 }}>
          {c.name || c.email || 'Без имени'}
        </span>
        <Pill tone={c.is_guest ? 'neutral' : 'info'} size="sm">
          {c.is_guest ? 'Гость' : 'Зарегистрирован'}
        </Pill>
      </div>

      <Row label="Телефон">
        {c.phone
          ? <a className="link" href={`tel:${c.phone}`}>{c.phone_display || c.phone}</a>
          : <Missing />}
      </Row>
      {/* Второй номер показываем, ТОЛЬКО если он другой: клиент мог оставить по
          заявке запасной телефон, и оператору важно знать, куда звонить. */}
      {c.profile_phone && (
        <Row label="В профиле">
          <a className="link" href={`tel:${c.profile_phone}`}>
            {c.profile_phone_display || c.profile_phone}
          </a>
        </Row>
      )}
      <Row label="Email">
        {c.email ? <a className="link" href={`mailto:${c.email}`}>{c.email}</a> : <Missing />}
      </Row>

      {c.is_guest ? (
        <Row label="Аккаунт">
          {/* Контакты гостя НИКЕМ не подтверждены: почту и телефон на /help
              вводит кто угодно. Совпадение — повод проверить, кто перед нами,
              а не основание обсуждать данные аккаунта. */}
          <span className="t-caption">
            {!c.email
              ? 'Почта не указана — сверить с аккаунтом не с чем. Контакты гость оставил сам и не подтверждены.'
              : c.known_account
                ? 'Есть аккаунт с этой почтой. Контакты гость оставил сам — прежде чем говорить о данных аккаунта, убедитесь, что это его владелец.'
                : 'Аккаунта с такой почтой нет. Контакты гость оставил сам и не подтверждены.'}
          </span>
        </Row>
      ) : (
        <>
          <Row label="Аккаунт">
            <span className="cluster cluster-8" style={{ flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span className="t-caption">
                {c.date_joined ? `с ${fmtDay(c.date_joined)}` : 'дата регистрации неизвестна'}
                {' · '}
                {ws.length === 0 ? 'без пространств'
                  : `пространств ${ws.length}${paid ? `, платных ${paid}` : ''}`}
              </span>
              {ws.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setOpenDossier((v) => !v)}>
                  {openDossier ? 'Свернуть' : 'Подробнее'}
                </Button>
              )}
            </span>
          </Row>
          {openDossier && ws.length > 0 && (
            <div className="stack stack-1"
              style={{ maxHeight: 160, overflowY: 'auto', paddingRight: 4 }}>
              {ws.map((w) => (
                <span key={w.id} className="t-caption">
                  {w.name} · {WS_KIND[w.kind] || w.kind}
                  {w.is_owner ? ' · владелец' : ''}
                  {' — '}
                  {w.plan || 'без тарифа'} ({w.plan_state})
                  {w.expires_at ? `, до ${fmtDay(w.expires_at)}` : ''}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SupportThreadDrawer({ threadId, onClose }) {
  const toast = useToast();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [busyClose, setBusyClose] = useState(false);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    try { setThread(await api.getThread(threadId)); }
    catch { toast.show('Не удалось загрузить', 'danger'); }
    finally { setLoading(false); }
  }, [threadId, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [thread]);

  const reply = async (body, files) => {
    // Причину показываем словами сервера (e.detail), а не общим «Не отправилось»:
    // ответ с вложением может отвалиться на лимите/типе файла, и глухой тост
    // превращал такой отказ в «просто не работает».
    try { setThread(await api.reply(threadId, body, files)); }
    catch (e) {
      toast.show((e && e.detail) || 'Не отправилось', 'danger');
      throw e;  // композер чистит поле только после успеха — набранный ответ не теряем
    }
  };

  const doClose = async () => {
    const r = reason.trim();
    if (!r || busyClose) return;
    setBusyClose(true);
    try {
      setThread(await api.closeThread(threadId, r));
      setClosing(false); setReason('');
      toast.show('Заявка закрыта', 'muted');
    } catch { toast.show('Не удалось закрыть', 'danger'); }
    finally { setBusyClose(false); }
  };

  const closed = thread && thread.status === 'closed';
  const st = thread ? (STATUS[thread.status] || { label: thread.status, tone: 'neutral' }) : null;

  return (
    <Drawer open onClose={onClose} size="md" breadcrumbs="Поддержка"
      title={thread ? (thread.user_label || thread.user_email || 'Заявка') : 'Заявка'}
      footer={
        closed ? (
          <div className="t-caption text-mute" style={{ width: '100%', textAlign: 'center' }}>Заявка закрыта.</div>
        ) : closing ? (
          <div className="stack stack-2" style={{ width: '100%' }}>
            <Textarea minRows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Причина закрытия — внутренняя, пользователю НЕ показывается…" />
            <div className="cluster cluster-8">
              <Button variant="primary" onClick={doClose} disabled={busyClose || !reason.trim()}>Закрыть заявку</Button>
              <Button variant="ghost" onClick={() => { setClosing(false); setReason(''); }}>Отмена</Button>
            </div>
          </div>
        ) : (
          <div style={{ width: '100%' }}><Composer onSend={reply} placeholder="Ответ пользователю…" /></div>
        )
      }>
      {loading ? (
        <div className="center pad-24"><Spinner /></div>
      ) : thread && (
        <div className="stack stack-4">
          <ClientCard c={thread.client} />
          <div className="cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Pill tone={st.tone} size="sm">{st.label}</Pill>
            <span className="grow" />
            {!closed && (
              <Button variant="ghost" size="sm" iconLeft="check" onClick={() => setClosing(true)}>
                Закрыть заявку
              </Button>
            )}
          </div>
          {closed && thread.closed_reason && (
            <div className="t-caption" style={{ background: '#fff7ed', border: '1px solid #fed7aa',
                 borderRadius: 8, padding: '8px 10px' }}>
              <b>Причина закрытия (внутренняя):</b> {thread.closed_reason}
            </div>
          )}
          <div ref={bodyRef} style={{ maxHeight: '52vh', overflowY: 'auto', paddingRight: 4 }}>
            <MessageList messages={thread.messages} mineSide="operator" />
          </div>
        </div>
      )}
    </Drawer>
  );
}

export { SupportThreadDrawer };
